import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';

/**
 * 通用 OAuth 登录回调处理件
 * 处理后端重定向回来的 token, role, is_new 等参数
 */
const OAuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const hasAttempted = React.useRef(false);
  const verifyLock = React.useRef(false);

  useEffect(() => {
    if (hasAttempted.current || verifyLock.current) return;

    const token = searchParams.get('token');
    const role = searchParams.get('role') || 'user';
    const isNew = searchParams.get('is_new') === 'true';
    const authError = searchParams.get('error');

    if (authError) {
      console.error('OAuth Callback Error from backend:', authError);
      const errorMsg = t(`auth.${authError}`) !== `auth.${authError}` 
        ? t(`auth.${authError}`) 
        : `认证接口返回错误: ${authError}`;
      setError(errorMsg);
      return;
    }

    if (!token) {
      console.error('OAuth Callback: Missing token in URL');
      setError(t('auth.error_missing_token') || '登录失败：缺失认证令牌。');
      return;
    }

    // Lock it
    verifyLock.current = true;

    // クーポン自動バインドヘルパー（sessionStorage に保留中コードがある場合のみ実行）
    const bindPendingCoupon = async () => {
      const code = sessionStorage.getItem('pending_coupon_code');
      if (!code) return;
      sessionStorage.removeItem('pending_coupon_code');
      try {
        await api.post('/user/coupons/bind', { code });
        toast.success(t('auth.coupon_bound', { code }));
      } catch { /* 既にバインド済み or 期限切れ → 無視 */ }
    };

    // 非同期処理を IIFE でラップ（useEffect コールバックは async 不可）
    (async () => {
      try {
        // 1. 调用 AuthContext 的登录方法保存 JWT 和角色
        login(token, role, 'wechat');

        // 2. 保留中クーポンがあれば自動バインド（一般ユーザーのみ）
        if (role === 'user') {
          await bindPendingCoupon();
        }

        // 3. 提示成功
        toast.success(t('auth.toast_login_success') || '登录成功！正在跳转...');

        hasAttempted.current = true;

        // 4. 根据角色和新老用户状态进行跳转
        setTimeout(() => {
          if (role === 'admin') {
            navigate('/admin/dashboard', { replace: true });
          } else if (role === 'doctor') {
            navigate('/doctor/dashboard', { replace: true });
          } else {
            // 法人リンク経由なら社員番号入力へ直接戻す(/mypage を経由せずバウンス回避)
            const corp = sessionStorage.getItem('pending_corp_code');
            if (corp && /^[A-Z0-9_-]+$/.test(corp)) {
              navigate(`/corp/${corp}`, { replace: true });
            } else if (isNew) {
              navigate('/welcome', { replace: true });
            } else {
              navigate('/mypage', { replace: true });
            }
          }
        }, 1000);

      } catch (err) {
        console.error('OAuth Login Error:', err);
        setError('无法处理登录状态，请重试。');
        verifyLock.current = false; // Reset if error occurred before completion
      }
    })();
  }, [searchParams, login, navigate, t]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center border-t-4 border-red-500">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('auth.login_failed') || '登录失败'}</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-coral text-white font-bold py-3 px-4 rounded-lg hover:opacity-90 transition duration-200"
          >
            {t('common.return_home') || '返回首页'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mb-4"></div>
        <p className="text-gray-600 text-lg">正在安全登录，请稍候...</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
